import { useEffect, useState } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";

interface SaveViewModalProps {
  opened: boolean;
  mode: "create" | "rename";
  initialName: string;
  isPending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (name: string) => void;
  onClearError: () => void;
}

export const SaveViewModal = ({
  opened,
  mode,
  initialName,
  isPending,
  error,
  onClose,
  onSubmit,
  onClearError
}: SaveViewModalProps) => {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (opened) setName(initialName);
  }, [opened, initialName]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !isPending;
  const isRename = mode === "rename";

  const handleChange = (value: string) => {
    setName(value);
    if (error) onClearError();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-full max-w-[420px]"
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-content-1000">
            {isRename ? "Rename view" : "Save view"}
          </h2>
          <IconButton
            aria-label="Close"
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.SM}
            onClick={onClose}
            variant={IconButtonVariant.TEXT_BASE}
          />
        </div>

        <Show if={!isRename}>
          <p className="text-sm font-medium text-content-400 mb-4">
            This view will include the filters and columns that are currently
            applied.
          </p>
        </Show>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            onSubmit(trimmed);
          }}
        >
          <BaseInput
            id="saved-view-name"
            label="Name"
            placeholder="Name your view"
            value={name}
            onChange={handleChange}
            error={error || undefined}
            hideOptional
            autoFocus
            maxLength={255}
          />

          <div className="grid grid-cols-2 gap-3 mt-5">
            <Button
              type="button"
              size={ButtonSize.BASE}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              disabled={!canSubmit}
              loading={isPending}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
